/*************************
 * @license
 * Copyright 2024 Myenergi Ltd. All rights reserved.
 * No part of this work may be reproduced, stored in a retrieval system of any nature, or transmitted, in any form or by any means without the prior written permission of Myenergi Ltd., the copyright owner.
 * If any unauthorised acts are carried out in relation to this copyright work, a civil claim for damages may be made and/or a criminal prosecution may result.
 *************************/
import { InitialRowsPerPage } from "components/Tables/useTableState";
import { Installer } from "containers/Installers/types/Installer";
import { t } from "i18next";
import { action, makeObservable, observable } from "mobx";
import { persist } from "mobx-persist";
import { get } from "utils/apiMethods";
import { AlertType, NotificationStore } from "./NotificationStore";

interface InstallersResult {
  installers: Installer[];
  totalCount: number;
}

export class InstallersStore {
  private notificationStore;

  public constructor(notification: NotificationStore) {
    this.notificationStore = notification;
    this.rowsPerPage = 25;
    this.installerSearchTerm = "";
    this.installersList = [];
    this.installersFiltered = [];

    makeObservable(this);
  }

  @observable @persist("object") public installersList: Installer[];
  @observable @persist("object") public installersFiltered: Installer[];
  @observable @persist("object") public selectedInstaller:
    | Installer
    | undefined;
  @observable @persist("object") public installerSearchTerm: string;
  @observable @persist("object") public rowsPerPage: InitialRowsPerPage;

  @observable @persist public isLoading = false;
  @observable @persist public totalCount = 0;

  @action.bound
  public setIsLoading(isLoading: boolean): void {
    if (isLoading) {
      this.setInstallersList([]);
    }

    this.isLoading = isLoading;
  }

  @action.bound
  public search = async () => {
    this.setIsLoading(true);

    try {
      const paramsObj = {
        limit: this.rowsPerPage.toString(),
        emailFilter: this.installerSearchTerm,
      };
      const params = new URLSearchParams(paramsObj);
      const url = `installers?${params.toString()}`;
      const result = (await get(url)) as InstallersResult;

      this.setInstallersList(result.installers);
      this.setTotalCount(result.totalCount);
    } catch (e) {
      this.notificationStore.triggerNotification(
        t("installersPage.loadInstallersError"),
        AlertType.error,
      );
    } finally {
      this.setIsLoading(false);
    }
  };

  @action.bound
  public setInstallersList(installers: Installer[]) {
    this.installersList = installers;
  }

  @action.bound
  public setTotalCount(total: number) {
    this.totalCount = total;
  }

  @action.bound
  public setSelectedInstaller(installer?: Installer): void {
    this.selectedInstaller = installer;
  }

  @action.bound
  public setInstallerSearchTerm(searchTerm: string): void {
    this.installerSearchTerm = searchTerm;
    this.search();
  }

  @action.bound
  public setRowsPerPage(value: InitialRowsPerPage): void {
    this.rowsPerPage = value;
    this.search();
  }
}
